import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight, Loader2, Sparkles, Check } from "lucide-react";
import { TAB_SKILL_MAP, STAGE_META, type StudioTab } from "@/lib/ccbSkillMap";
import { useAuth } from "../context/AuthContext";
import { useParams } from "react-router-dom";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

export function ccbNoteKey(projectId: string, tab: string) {
  return `ccb-note:${projectId}:${tab}`;
}

export interface CCBSavedNote {
  tab: string;
  notes: string;
  savedAt: string;
}

interface HintState {
  loading: boolean;
  summary: string;
  steps: string[];
  notes: string;
  saved: boolean;
}

interface Props {
  tab: StudioTab;
  className?: string;
}

export default function CCBSkillHint({ tab, className }: Props) {
  const { token } = useAuth();
  const { id: projectId } = useParams<{ id: string }>();
  const storageKey = projectId ? ccbNoteKey(projectId, tab) : null;

  const [panelOpen, setPanelOpen] = useState(false);
  const [hint, setHint] = useState<HintState>({
    loading: false, summary: "", steps: [], notes: "", saved: false,
  });

  // Load persisted note on mount
  useEffect(() => {
    if (!storageKey) return;
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      try {
        const parsed: CCBSavedNote = JSON.parse(raw);
        setHint(h => ({ ...h, notes: parsed.notes }));
      } catch {}
    }
  }, [storageKey]);

  const mapping = TAB_SKILL_MAP[tab];
  if (!mapping || mapping.skills.length === 0) return null;

  const uniqueStages = mapping.skills
    .map(s => s.stage)
    .filter((v, i, arr) => arr.indexOf(v) === i);

  async function fetchHint() {
    if (hint.steps.length > 0) return;
    setHint(h => ({ ...h, loading: true }));
    try {
      const { data } = await axios.post(
        `${BASE}/api/tools/skill-guide`,
        { tab, skills: mapping.skills.map(s => ({ slug: s.slug, label: s.label, description: s.description })) },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setHint(h => ({ ...h, loading: false, summary: data.summary ?? "", steps: data.steps ?? [] }));
    } catch {
      setHint(h => ({ ...h, loading: false, summary: "Could not load guidance. Try again.", steps: [] }));
    }
  }

  function togglePanel() {
    const next = !panelOpen;
    setPanelOpen(next);
    if (next) fetchHint();
  }

  function saveNote() {
    if (!storageKey || !hint.notes.trim()) return;
    const note: CCBSavedNote = { tab, notes: hint.notes.trim(), savedAt: new Date().toISOString() };
    localStorage.setItem(storageKey, JSON.stringify(note));
    setHint(h => ({ ...h, saved: true }));
    setTimeout(() => setHint(h => ({ ...h, saved: false })), 2000);
  }

  return (
    <div className={cn("overflow-hidden rounded-xl border", className)}
      style={{ borderColor: "#D9D4CC", backgroundColor: "#FCFBF8" }}>

      {/* Header */}
      <button
        onClick={togglePanel}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left transition-colors"
        style={{ backgroundColor: panelOpen ? "#F0ECE5" : "#FCFBF8" }}
      >
        <div className="flex items-center gap-2.5">
          <div className="flex gap-0.5">
            {uniqueStages.map(stage => (
              <span key={stage} className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: STAGE_META[stage].color }} title={STAGE_META[stage].label} />
            ))}
          </div>
          <span className="text-[11px] font-mono uppercase tracking-widest" style={{ color: "#5C5C57" }}>
            Skill Lens
          </span>
          <span style={{ color: "#D9D4CC" }}>·</span>
          <span className="text-[11px] truncate max-w-[240px]" style={{ color: "#5C5C57" }}>
            {mapping.why}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-medium"
            style={{
              backgroundColor: hint.steps.length > 0 ? "#27313D" : "transparent",
              color: hint.steps.length > 0 ? "#F7F4EE" : "#314E6E",
              border: `1px solid ${hint.steps.length > 0 ? "#27313D" : "#314E6E"}`,
            }}>
            {hint.loading ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Sparkles className="h-2.5 w-2.5" />}
            Hint
          </span>
          {panelOpen
            ? <ChevronDown className="h-3.5 w-3.5" style={{ color: "#5C5C57" }} />
            : <ChevronRight className="h-3.5 w-3.5" style={{ color: "#5C5C57" }} />}
        </div>
      </button>

      {/* Expanded panel */}
      {panelOpen && (
        <div style={{ borderTop: "1px solid #D9D4CC", backgroundColor: "#F7F4EE" }}>

          {/* AI hint */}
          {(hint.loading || hint.summary || hint.steps.length > 0) && (
            <div className="px-4 py-4" style={{ borderBottom: "1px solid #D9D4CC" }}>
              {hint.loading && (
                <div className="flex items-center gap-2" style={{ color: "#5C5C57" }}>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span className="text-[12px]">Generating hint…</span>
                </div>
              )}
              {!hint.loading && hint.summary && (
                <p className="italic mb-3" style={{ fontSize: 12, color: "#5C5C57", lineHeight: 1.7 }}>
                  {hint.summary}
                </p>
              )}
              {!hint.loading && hint.steps.length > 0 && (
                <ol className="space-y-2">
                  {hint.steps.map((step, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="font-mono font-bold shrink-0 mt-0.5"
                        style={{ fontSize: 11, color: "#A37B32", minWidth: 16 }}>{i + 1}.</span>
                      <span style={{ fontSize: 12, color: "#1E1E1C", lineHeight: 1.7 }}>{step}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}

          {/* Skill cards */}
          <div className="p-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {mapping.skills.map(skill => {
              const stage = STAGE_META[skill.stage];
              return (
                <div key={skill.slug} className="flex flex-col p-3.5 rounded-xl"
                  style={{ backgroundColor: "#FCFBF8", border: "1px solid #D9D4CC", borderTop: `3px solid ${stage.color}` }}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded"
                      style={{ backgroundColor: stage.color + "22", color: stage.color }}>{skill.stage}</span>
                    {skill.isNew && (
                      <span className="text-[9px] font-mono px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: "#e6f4ea", color: "#2d6a4f" }}>NEW</span>
                    )}
                  </div>
                  <p className="font-semibold leading-snug mb-1" style={{ fontSize: 13, color: "#1E1E1C" }}>{skill.label}</p>
                  <p style={{ fontSize: 11, color: "#5C5C57", lineHeight: 1.6 }}>{skill.description}</p>
                  <p className="font-mono mt-2" style={{ fontSize: 10, color: "#A37B32" }}>{skill.slug}</p>
                </div>
              );
            })}
          </div>

          {/* Notes + Save */}
          <div className="px-4 pb-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-mono uppercase tracking-widest" style={{ fontSize: 10, color: "#5C5C57" }}>
                Your notes
              </span>
              <button
                onClick={saveNote}
                disabled={!hint.notes.trim()}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-mono font-medium transition-colors disabled:opacity-40"
                style={{
                  backgroundColor: hint.saved ? "#4E6654" : "#314E6E",
                  color: "#F7F4EE",
                }}
              >
                {hint.saved ? <><Check className="h-2.5 w-2.5" /> Saved</> : "Save for later →"}
              </button>
            </div>
            <textarea
              rows={3}
              value={hint.notes}
              onChange={e => setHint(h => ({ ...h, notes: e.target.value, saved: false }))}
              placeholder="Add your judgment, context, or adaptations…"
              className="w-full resize-y rounded-lg px-3 py-2 text-[12px] leading-relaxed outline-none"
              style={{
                backgroundColor: "#F7F4EE",
                border: "1px solid #D9D4CC",
                color: "#1E1E1C",
                fontFamily: "inherit",
              }}
              onFocus={e => (e.target.style.borderColor = "#314E6E")}
              onBlur={e => (e.target.style.borderColor = "#D9D4CC")}
            />
          </div>
        </div>
      )}
    </div>
  );
}
